// Required Evidence API

import { EvidenceRequestStatus } from "../../constant/requiredEvidence";
import { formatDate } from "../../utils/date";

export type AttachmentCategory = "DOC" | "INF";

export type RqdEvidenceTypeOverview = {
  code: string;
  description: string;
  attachmentCategory: AttachmentCategory;
  guidance: string;
  knowledgeBaseURL: string;
}

export type AmlDocumentCategory = {
  code: string;
  description: string;
  docVaultCategory: string;
}

export type RqdEvidenceTypeWithDocCategories = RqdEvidenceTypeOverview & {
  documentCategories: AmlDocumentCategory[];
}

export type InvestorEvidenceRequestOverview = RqdEvidenceTypeOverview & {
  status: string;
  dateUpdated?: string;
  updatedBy?: string;
  realRequest: boolean;
}

export type EvidenceRequestDocument = {
  urn: string;
  updatedOnDate: string;
  updatedOnTime: string;
  amlTypeCode: string;
  amlTypeDesc: string;
  otherTypeDesc: string;
  filename: string;
  createdByUser: string;
  combined: boolean;
}

export type InvestorEvidenceRequestDetail = {
  code: string;
  attachmentCategory: AttachmentCategory;
  status: string;
  updatedByUser: string;
  updatedOnDate: string;
  updatedOnTime: string;
  reviewerComment: string;
  submitterComment: string;
  createdByUser: string;
  documents: EvidenceRequestDocument[];
}

export type DocumentUpdateActions = "add" | "delete";

export type RequiredEvidenceDocumentUpdatePayload = {
  action?: DocumentUpdateActions;
  category: string;
  documentName: string;
  documentURN: string;
  otherTypeDescription?: string;
}

export type EvidenceRequestUpdatePayload = {
  evidenceStatus: string;
  evidenceTypeCode: string;
  reviewerComment: string;
  submitterComment: string;
  documents: RequiredEvidenceDocumentUpdatePayload[];
}

export function requiredEvidenceTypesTransformer(data: any): RqdEvidenceTypeWithDocCategories {
  return {
    code: data.typeCode,
    description: data.description,
    attachmentCategory: data.category,
    guidance: data.guidanceText,
    knowledgeBaseURL: data.knowledgeBaseURL,
    documentCategories: data.documentTypes.map((el: any): AmlDocumentCategory => {
      return {
        code: el.docTypeCode,
        description: el.docTypeDescription,
        docVaultCategory: el.docVaultDocCategory,
      };
    })
  };
}

export function investorEvidenceRequestTransformer(rows: any, types: RqdEvidenceTypeWithDocCategories[]): InvestorEvidenceRequestOverview[] {
  return types.map((type) => {
    const request = rows.find((el: any) => el.evidenceTypeCode === type.code);
    if (request) {
      return {
        ...type,
        status: request.evidenceStatus,
        dateUpdated: request.updatedOnDate,
        updatedBy: request.updatedByUser,
        realRequest: true,
      };
    }
    return {
      ...type,
      realRequest: false,
      status: EvidenceRequestStatus.NRQ
    };
  });
}

export type EvidenceRequestHistoryItem = {
  statusApplied: string;
  description: string;
  operator: string;
  createdDate: string;
  createdTime: string;
  comments: string;
}

export function evidenceRequestHistoryTransformer(row: any): EvidenceRequestHistoryItem {
  return {
    operator: row.createdByUser,
    createdDate: row.createdOnDate,
    createdTime: row.createdOnTime,
    description: row.eventDescription,
    comments: row.comment,
    statusApplied: row.statusApplied
  };
}

export function investorEvidenceRequestDetailTransformer(row: any): InvestorEvidenceRequestDetail {
  return {
    code: row.evidenceTypeCode,
    attachmentCategory: row.category,
    status: row.evidenceStatus,
    updatedByUser: row.updatedByUser,
    updatedOnDate: row.updatedOnDate,
    updatedOnTime: row.updatedOnTime,
    reviewerComment: row.reviewerComment,
    submitterComment: row.submitterComment,
    createdByUser: row.createdByUser,
    documents: row.documents.map((el: any): EvidenceRequestDocument => {
      return {
        urn: el.documentURN,
        updatedOnDate: formatDate(el.updatedOnDate),
        updatedOnTime: el.updatedOnTime,
        amlTypeCode: el.documentTypeCode,
        amlTypeDesc: el.documentTypeDesc,
        otherTypeDesc: el.otherTypeDescription,
        filename: el.documentName,
        createdByUser: el.createdByUser,
        combined: el.combinedDocStatus === "Y"
      };
    })
  };
}
